/**
 * Browser Data Capture Module
 * 
 * Captures rich browser/environment data that matches what GA4's gtag automatically provides.
 * This ensures server-side analytics events have the same data richness as direct gtag events.
 */

// Mobile detection utilities (copied from SharedComponents to avoid circular dependency)
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const isAppleDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for macOS
  const isMac = /macintosh|mac os x/.test(userAgent);

  // Check for Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Check for iPad that identifies as MacOS in newer versions
  const isIPadOS = isMac && navigator.maxTouchPoints > 0;

  return isIOS || isMac || isIPadOS || isSafari;
};

const isMobileDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTouch = isTouchDevice();

  // Check for mobile user agents
  const isMobileUA = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent);

  // Check for tablets specifically
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

  // Check for small screens (mobile breakpoint)
  const maxWidth = window.matchMedia('(max-width: 768px)').matches;

  // Check if it's an Apple device (includes iPad detection)
  const isApple = isAppleDevice();

  // IMPORTANT: When users request "Desktop site" on mobile browsers:
  // - User agent changes to desktop (e.g., "iPhone" -> "Macintosh")
  // - Touch capability remains available
  // - The key indicator is the absence of mobile markers in UA
  //
  // To properly detect desktop mode requested on mobile:
  // - If UA doesn't contain mobile indicators (iPhone, Android, etc.) BUT device has touch
  // - Then user likely requested desktop mode, so treat as desktop

  const hasMobileIndicator = isMobileUA || isTablet;

  // If no mobile indicator in UA but has touch, user likely requested desktop mode
  // Exception: still show mobile UI if screen is very small (< 768px)
  if (!hasMobileIndicator && isTouch && !maxWidth) {
    return false;
  }

  // Return true if:
  // 1. It's a touch device AND
  // 2. Either:
  //    - It's a mobile phone (mobile UA)
  //    - It's a tablet (tablet UA or iPad)
  //    - It's a small touch screen device (maxWidth)
  //    - It's an Apple device with mobile indicators
  return isTouch && (isMobileUA || isTablet || (isApple && (hasMobileIndicator || maxWidth)));
};

export interface BrowserData {
  // GA4 system fields (top-level, matching BigQuery schema)
  user_pseudo_id: string | null; // GA4 client ID
  
  // Device data (nested structure matching GA4 BigQuery schema)
  device: {
    category: string;
    mobile_brand_name?: string;
    mobile_model_name?: string;
    operating_system: string;
    operating_system_version: string;
    language: string;
    time_zone_offset_seconds: number;
    browser: string;
    browser_version: string;
    web_info: {
      browser: string;
      browser_version: string;
      hostname: string;
    };
  };
  
  // Privacy info (nested structure matching GA4 BigQuery schema)
  privacy_info: {
    analytics_storage: string;
    ads_storage: string;
    uses_transient_token: string;
  };
  
  // Traffic source (nested structure matching GA4 BigQuery schema)
  traffic_source: {
    name: string;
    medium: string;
    source: string;
  };
  
  // Collected traffic source (for Google Ads click IDs)
  collected_traffic_source: {
    [key: string]: string; // Dynamic keys based on what's available
  };
  
  // Platform classification (top-level)
  platform: string;
  
  // Event dimensions (nested structure matching GA4 BigQuery schema)
  event_dimensions: {
    hostname: string;
  };
  
  // Additional browser context (for debugging and backward compatibility)
  _browser_context: {
    screen_resolution: string;
    screen_width: number;
    screen_height: number;
    color_depth: number;
    pixel_ratio: number;
    page_location: string;
    page_title: string;
    page_path: string;
    page_search: string;
    referrer: string;
    viewport_width: number;
    viewport_height: number;
    connection_type: string;
    user_agent: string;
    time_zone: string;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    utm_term: string | null;
    utm_content: string | null;
    utm_string: string | null;
    ismobile: boolean;
  };
}

/**
 * Capture browser/environment data that gtag automatically provides
 */
export function captureBrowserData(): BrowserData {
  // Check if we're in a browser environment
  // If not (e.g., Node.js server), return minimal safe data
  if (typeof window === 'undefined') {
    return {
      user_pseudo_id: null,
      device: {
        category: 'server',
        operating_system: 'Node.js',
        operating_system_version: process.version || 'unknown',
        language: 'en-US',
        time_zone_offset_seconds: 0,
        browser: 'Node.js',
        browser_version: process.version || 'unknown',
        web_info: {
          browser: 'Node.js',
          browser_version: process.version || 'unknown',
          hostname: 'server',
        },
      },
      privacy_info: {
        analytics_storage: 'Yes',
        ads_storage: 'No',
        uses_transient_token: 'No',
      },
      traffic_source: {
        name: '(server)',
        medium: '(none)',
        source: '(server)',
      },
      collected_traffic_source: {},
      platform: 'Node.js',
      event_dimensions: {
        hostname: 'server',
      },
      _browser_context: {
        screen_resolution: '0x0',
        screen_width: 0,
        screen_height: 0,
        color_depth: 0,
        pixel_ratio: 1,
        page_location: '',
        page_title: '',
        page_path: '',
        page_search: '',
        referrer: '',
        viewport_width: 0,
        viewport_height: 0,
        connection_type: 'unknown',
        user_agent: 'Node.js',
        time_zone: 'UTC',
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_term: null,
        utm_content: null,
        utm_string: null,
        ismobile: false,
      },
    };
  }

  const userAgent = navigator.userAgent;
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const screenWidth = screen.width;
  const screenHeight = screen.height;
  const colorDepth = screen.colorDepth;
  const pixelRatio = window.devicePixelRatio;
  
  // Parse URL for referrer and UTM parameters
  const url = new URL(window.location.href);
  const referrer = document.referrer;
  const utmSource = url.searchParams.get('utm_source');
  const utmMedium = url.searchParams.get('utm_medium');
  const utmCampaign = url.searchParams.get('utm_campaign');
  const utmTerm = url.searchParams.get('utm_term');
  const utmContent = url.searchParams.get('utm_content');
  
  // UTM string management - capture, store, and retrieve
  const utmString = getAndStoreUtmString(url);
  
  // Get page location data
  const pageLocation = window.location.href;
  const pageTitle = document.title;
  const pageHostname = window.location.hostname;
  const pagePath = window.location.pathname;
  const pageSearch = window.location.search;
  
  // Enhanced device detection
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android(?=.*\bMobile\b)/i.test(userAgent);
  const deviceCategory = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';
  
  // Comprehensive mobile detection using SharedComponents utility
  // This matches the same logic used in marketing/demo sites for consistency
  const isMobileAdvanced = isMobileDevice();
  
  // Enhanced OS detection
  const osInfo = getDetailedOSInfo(userAgent);
  
  // Timezone offset in seconds (like GA4 expects)
  const timezoneOffsetSeconds = new Date().getTimezoneOffset() * -60;
  
  // Enhanced traffic source detection
  const trafficSource = getTrafficSourceInfo(url, referrer);
  
  // Check for Google Ads click IDs
  const gclid = url.searchParams.get('gclid');
  const dclid = url.searchParams.get('dclid');
  const srsltid = url.searchParams.get('srsltid');
  
  // Capture GA4's client ID for consistent user tracking
  const gaClientId = getGA4ClientId();

  // Structure data to match GA4 BigQuery schema exactly
  const browserData: BrowserData = {
    // GA4 system fields (top-level)
    user_pseudo_id: gaClientId, // This is the GA4 client ID
    
    // Device data (nested structure matching GA4 schema)
    device: {
      category: deviceCategory,
      ...(isMobile && { 
        mobile_brand_name: getMobileBrand(userAgent),
        mobile_model_name: getMobileModel(userAgent)
      }),
      operating_system: getOSInfo(userAgent),
      operating_system_version: osInfo.version,
      language: language,
      time_zone_offset_seconds: timezoneOffsetSeconds,
      browser: getBrowserInfo(userAgent),
      browser_version: getBrowserVersion(userAgent),
      web_info: {
        browser: getBrowserInfo(userAgent),
        browser_version: getBrowserVersion(userAgent),
        hostname: pageHostname
      }
    },
    
    // Privacy info (nested structure matching GA4 schema)
    privacy_info: {
      analytics_storage: 'granted', // Assume granted for now
      ads_storage: 'granted', // Assume granted for now
      uses_transient_token: 'false'
    },
    
    // Traffic source (nested structure matching GA4 schema)
    traffic_source: {
      name: trafficSource.name,
      medium: trafficSource.medium,
      source: trafficSource.source
    },
    
    // Collected traffic source (for Google Ads click IDs) - only include non-undefined values
    collected_traffic_source: {
      ...(utmCampaign && { manual_campaign_name: utmCampaign }),
      ...(utmSource && { manual_source: utmSource }),
      ...(utmMedium && { manual_medium: utmMedium }),
      ...(utmTerm && { manual_term: utmTerm }),
      ...(utmContent && { manual_content: utmContent }),
      ...(gclid && { gclid }),
      ...(dclid && { dclid }),
      ...(srsltid && { srsltid })
    },
    
    // Platform classification (top-level)
    platform: 'web', // Always web for browser-based analytics
    
    // Event dimensions (nested structure matching GA4 schema)
    event_dimensions: {
      hostname: pageHostname
    },
    
    // Additional browser context (for debugging and compatibility)
    // These will be included in eventParams for backward compatibility
    _browser_context: {
      screen_resolution: `${screenWidth}x${screenHeight}`,
      screen_width: screenWidth,
      screen_height: screenHeight,
      color_depth: colorDepth,
      pixel_ratio: pixelRatio,
      page_location: pageLocation,
      page_title: pageTitle,
      page_path: pagePath,
      page_search: pageSearch,
      referrer: referrer,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      connection_type: (navigator as any).connection?.effectiveType || 'unknown',
      user_agent: userAgent,
      time_zone: timezone,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_term: utmTerm,
      utm_content: utmContent,
      utm_string: utmString,  // Full UTM string for persistence tracking
      ismobile: isMobileAdvanced  // Comprehensive mobile detection matching marketing site logic
    }
  };

  // Return browser data without logging (cleaner output)
  return browserData;
}

// Helper functions to parse browser and OS info
function getBrowserInfo(userAgent: string): string {
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Other';
}

function getBrowserVersion(userAgent: string): string {
  const browser = getBrowserInfo(userAgent);
  const match = userAgent.match(new RegExp(`${browser}/([\\d.]+)`));
  return match ? match[1] : 'unknown';
}

function getOSInfo(userAgent: string): string {
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS')) return 'iOS';
  return 'Other';
}

function getDetailedOSInfo(userAgent: string): { name: string; version: string } {
  let name = 'Other';
  let version = 'unknown';
  
  if (userAgent.includes('Windows NT')) {
    name = 'Windows';
    const match = userAgent.match(/Windows NT ([0-9.]+)/);
    if (match) {
      const ntVersion = match[1];
      if (ntVersion === '10.0') version = '10';
      else if (ntVersion === '6.3') version = '8.1';
      else if (ntVersion === '6.2') version = '8';
      else if (ntVersion === '6.1') version = '7';
      else version = ntVersion;
    }
  } else if (userAgent.includes('Mac OS X')) {
    name = 'macOS';
    const match = userAgent.match(/Mac OS X ([0-9_]+)/);
    if (match) {
      version = match[1].replace(/_/g, '.');
    }
  } else if (userAgent.includes('Android')) {
    name = 'Android';
    const match = userAgent.match(/Android ([0-9.]+)/);
    if (match) version = match[1];
  } else if (userAgent.includes('iPhone OS') || userAgent.includes('iPad')) {
    name = 'iOS';
    const match = userAgent.match(/OS ([0-9_]+)/);
    if (match) {
      version = match[1].replace(/_/g, '.');
    }
  } else if (userAgent.includes('Linux')) {
    name = 'Linux';
  }
  
  return { name, version };
}

function getMobileBrand(userAgent: string): string {
  if (userAgent.includes('iPhone')) return 'Apple';
  if (userAgent.includes('iPad')) return 'Apple';
  if (userAgent.includes('Samsung')) return 'Samsung';
  if (userAgent.includes('Google')) return 'Google';
  if (userAgent.includes('OnePlus')) return 'OnePlus';
  if (userAgent.includes('Huawei')) return 'Huawei';
  if (userAgent.includes('Xiaomi')) return 'Xiaomi';
  if (userAgent.includes('LG')) return 'LG';
  if (userAgent.includes('Sony')) return 'Sony';
  return 'Unknown';
}

function getMobileModel(userAgent: string): string {
  // This is a simplified version - real model detection is much more complex
  if (userAgent.includes('iPhone')) {
    const match = userAgent.match(/iPhone[^;]*/);
    return match ? match[0] : 'iPhone';
  }
  if (userAgent.includes('iPad')) {
    const match = userAgent.match(/iPad[^;]*/);
    return match ? match[0] : 'iPad';
  }
  // For Android, model detection is much more complex and requires additional parsing
  return 'Unknown';
}

function getGA4ClientId(): string | null {
  try {
    // Method 1: Try to parse from _ga cookie (most reliable)
    const gaCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    
    if (gaCookie) {
      const clientId = gaCookie.split('=')[1];
      
      // Extract the actual client ID from the cookie format: GA1.X.XXXXXXXXX.YYYYYYYYY (where X can be 1, 2, 3, etc.)
      const match = clientId.match(/GA1\.\d+\.([^.]+\.\d+)/);
      if (match) {
        return match[1];
      }
    }
    
    return null;
  } catch (error) {
    console.warn('[Analytics] Failed to get GA4 client ID:', error);
    return null;
  }
}

/**
 * UTM String Management System
 * Captures entire URL string only when it contains actual UTM-related parameters:
 * - Tilda parameters in path (~pn~google~cn~campaignname)
 * - Standard UTM parameters in query (utm_source, utm_medium, etc.)
 * Ignores system parameters like __clerk_handshake, __session, etc.
 */
function getAndStoreUtmString(url: URL): string | null {
  try {
    // Get the full URL string to check for UTM-related parameters
    const fullUrlString = url.toString();
    
    // Check for tilda parameters in the path (custom UTM format)
    const hasTildaParams = fullUrlString.includes('~');
    
    // Check for actual UTM parameters in query string (ignore system parameters)
    const hasUtmParams = url.searchParams.has('utm_source') || 
                        url.searchParams.has('utm_medium') || 
                        url.searchParams.has('utm_campaign') || 
                        url.searchParams.has('utm_term') || 
                        url.searchParams.has('utm_content');
    
    // Only capture if we have actual UTM-related parameters
    if (hasTildaParams || hasUtmParams) {
      // Store the entire URL string including both path and query parameters
      sessionStorage.setItem('playcast_utm_string', fullUrlString);
      return fullUrlString;
    }
    
    // No UTM params in current URL, check sessionStorage for stored UTM
    const storedUtmString = sessionStorage.getItem('playcast_utm_string');
    if (storedUtmString) {
      return storedUtmString;
    }
    
    // No UTM data available
    return null;
    
  } catch (error) {
    console.warn('[Analytics] Failed to manage UTM string:', error);
    return null;
  }
}


function getTrafficSourceInfo(url: URL, referrer: string): { name: string; medium: string; source: string } {
  const utmSource = url.searchParams.get('utm_source');
  const utmMedium = url.searchParams.get('utm_medium');
  const utmCampaign = url.searchParams.get('utm_campaign');
  
  // If UTM parameters exist, use them
  if (utmSource) {
    return {
      name: utmCampaign || 'direct',
      medium: utmMedium || 'none',
      source: utmSource
    };
  }
  
  // Otherwise, try to determine from referrer
  if (!referrer) {
    return { name: 'direct', medium: 'none', source: 'direct' };
  }
  
  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname.toLowerCase();
    
    if (hostname.includes('google')) {
      return { name: 'google', medium: 'organic', source: 'google' };
    }
    if (hostname.includes('bing')) {
      return { name: 'bing', medium: 'organic', source: 'bing' };
    }
    if (hostname.includes('facebook')) {
      return { name: 'facebook', medium: 'social', source: 'facebook' };
    }
    if (hostname.includes('twitter') || hostname.includes('x.com')) {
      return { name: 'twitter', medium: 'social', source: 'twitter' };
    }
    if (hostname.includes('linkedin')) {
      return { name: 'linkedin', medium: 'social', source: 'linkedin' };
    }
    
    // Generic referrer
    return { name: hostname, medium: 'referral', source: hostname };
  } catch {
    return { name: 'unknown', medium: 'referral', source: 'unknown' };
  }
}
